import type { DataMode } from './dataMode'

/**
 * The shapes of `GET|POST /api/admin/*`.
 *
 * Declared here, in `src/`, and imported by the server — the same direction `footballStatus.ts`
 * and `devToolCapabilities.ts` run in, and the only one available: nothing under `src/` may
 * import `server/football`. One shared type is what stops a panel and its payload drifting
 * apart, which on an operator surface means a blank field exactly when someone needs to read it.
 */

// ── The summary ───────────────────────────────────────────────────────────────────────────────

export type AlertSeverity = 'warn' | 'error'

/**
 * One thing that is wrong right now. `kind` is stable and machine-readable; `message` is written
 * for a person reading it at 3pm on a Saturday and is what the shell renders.
 */
export interface Alert {
  kind:
    | 'scheduler-paused'
    | 'scheduler-stopped'
    | 'budget-conserve'
    | 'budget-exhausted'
    | 'resource-failing'
    | 'fixture-stuck'
    | 'override-active'
    | 'no-fixtures'
  severity: AlertSeverity
  message: string
  /** Fixture id, resource name, … — whatever the alert is *about*, when it is about one thing. */
  target?: string
}

export interface AdminSummary {
  mode: DataMode
  driver: string
  season: number
  currentGameweek: number | null
  liveFixtureCount: number
  nextKickoffUtc: string | null
  deadlineUtc: string | null
  schedulerRunning: boolean
  serverTime: string
  alerts: Alert[]
}

// ── The audit log ─────────────────────────────────────────────────────────────────────────────

export interface AdminActionView {
  id: number
  actorEmail: string
  action: string
  target: string | null
  /** Already-parsed JSON, so the panel never parses a string it was handed. */
  before: unknown
  after: unknown
  reason: string | null
  mode: string
  createdAt: string
}

export interface AdminActionsPage {
  actions: AdminActionView[]
  /** Pass as `?before=` to fetch the next page; null when there is nothing older. */
  nextCursor: number | null
  /** Distinct action names present in the log, for the panel's filter. */
  actionNames: string[]
}

// ── The event feed ────────────────────────────────────────────────────────────────────────────

export type FeedScope = 'match' | 'system'
export type FeedSource = 'provider' | 'manual' | 'system'

export interface FeedEventView {
  /** Monotonic. The cursor every delivery path pages on. */
  id: number
  scope: FeedScope
  type: string
  fixtureId: string | null
  teamId: string | null
  minute: number | null
  homeScore: number | null
  awayScore: number | null
  meta: Record<string, unknown> | null
  source: FeedSource
  createdAt: string
  /** Denormalised so a row reads as football and not as a log line, with no second fetch. */
  homeTeamId: string | null
  awayTeamId: string | null
}

export interface FeedPage {
  events: FeedEventView[]
  /** The highest id the server holds, so a client with an empty page still knows where it is. */
  latestId: number
  /** True when the replay hit its cap and older events were dropped. */
  truncated: boolean
}

/** How many events one replay may carry, so a client returning after a week does not pull a month. */
export const FEED_REPLAY_CAP = 200

// ── Live matches ──────────────────────────────────────────────────────────────────────────────

export interface MatchView {
  id: string
  gameweek: number
  homeTeamId: string
  awayTeamId: string
  homeTeamName: string
  awayTeamName: string
  homeTeamAbbr: string
  awayTeamAbbr: string
  kickoffUtc: string
  status: string
  minute: number | null
  homeScore: number | null
  awayScore: number | null
  halfTimeHomeScore: number | null
  halfTimeAwayScore: number | null
  providerRef: string | null
  lastSyncedAt: string | null
  /** True when a manual override is in force on this fixture — badged everywhere. */
  overridden: boolean
  /** True past `MAX_MATCH_DURATION_MS` in play: the stuck-fixture condition. */
  stuck: boolean
}

export interface AdminMatches {
  /** Authoritative; the panel never trusts the browser clock. */
  serverTime: string
  gameweek: number | null
  deadlineUtc: string | null
  deadlinePassed: boolean
  live: MatchView[]
  upcoming: MatchView[]
  recent: MatchView[]
  lastLiveSyncAt: string | null
  /** Same rule as `/api/live`'s `LIVE_STALE_INTERVALS`. */
  stale: boolean
  liveWindowOpen: boolean | null
  liveWindowReason: string | null
  /** One line saying why nothing can be live in this mode, or null when something can be. */
  modeNote: string | null
}

/** Live matches carry their own recent events inline. */
export interface AdminMatchesWithEvents extends AdminMatches {
  eventsByFixture: Record<string, FeedEventView[]>
}

// ── Manual overrides ──────────────────────────────────────────────────────────────────────────

export interface OverrideView {
  fixtureId: string
  status: string | null
  homeScore: number | null
  awayScore: number | null
  halfTimeHomeScore: number | null
  halfTimeAwayScore: number | null
  kickoffUtc: string | null
  reason: string
  setByEmail: string
  createdAt: string
  updatedAt: string
  /** What the provider says, so the sheet always edits against a visible baseline. */
  provider: {
    status: string
    homeScore: number | null
    awayScore: number | null
    halfTimeHomeScore: number | null
    halfTimeAwayScore: number | null
    kickoffUtc: string
  } | null
  gameweek: number | null
  homeTeamId: string | null
  awayTeamId: string | null
}

export interface OverrideWriteResult {
  override: OverrideView
  /** True only when the gameweek has already been finalized, so a re-score is what it needs. */
  requiresRescore: boolean
  gameweek: number | null
  /** How many users' points change if it is re-scored. Computed before the write, shown with it. */
  affectedUsers: number
}

// ── Season & gameweek control ─────────────────────────────────────────────────────────────────

export interface GameweekControlState {
  gameweek: number
  deadlinePassed: boolean
  resultsRevealed: boolean
  fixtureCount: number
  finishedCount: number
  predictedUsers: number
}

export interface SchedulerControlState {
  paused: boolean
  pausedReason: string | null
  pausedUntil: string | null
  backoff: Record<string, { until: string; consecutiveFailures: number }>
  resyncEstimates: Record<string, number>
}

export interface AdminControlState {
  mode: DataMode
  gameweeks: GameweekControlState[]
  currentGameweek: number | null
  scheduler: SchedulerControlState
  /** Per-capability availability, so a refused control can explain itself rather than grey out. */
  capabilities: Record<string, { available: boolean; reason: string }>
}

// ── Pipeline health ───────────────────────────────────────────────────────────────────────────

export interface ProviderRequestsReport {
  days: number
  byDay: { day: string; total: number; failed: number }[]
  byEndpoint: { endpoint: string; total: number; failed: number }[]
  recentFailures: { endpoint: string; errorKind: string | null; requestedAt: string }[]
  dailyBudget: number
}

export interface DataCoverage {
  season: number
  fixturesByStatus: Record<string, number>
  gameweeksWithNoFixtures: number[]
  unmappedTeamRefs: string[]
  finishedWithoutDetail: string[]
  unknownStatusFixtures: string[]
}

// ── People & engagement ───────────────────────────────────────────────────────────────────────

export interface EngagementReport {
  gameweek: number
  fixtureCount: number
  users: number
  predicted: number
  complete: number
  partial: number
  none: number
  autoFilled: number
  bankerPicked: number
  questPicked: number
  chipsActive: { doubleUp: number; cleanSweep: number; goalfest: number }
  /** Counted separately: folding the 10 bots in makes every participation rate a lie. */
  bots: number
}

/** Response of the mock-friend purge — the same per-table shape a single delete
 *  returns, summed across every bot that had rows to remove. */
export interface PurgeMockFriendsResult {
  purged: number
  tables: Record<string, number>
}

export interface AdminUserView {
  id: string
  displayName: string
  email: string | null
  favouriteTeam: string | null
  joinedAt: string | null
  lastActiveAt: string | null
  leagues: number
  totalPoints: number
  isAdmin: boolean
  isBot: boolean
}

export interface AdminUserDetail {
  user: AdminUserView
  history: {
    gameweek: number
    points: number
    predicted: number
    autoFilled: number
    bankerGameId: string | null
  }[]
  leagues: { id: string; name: string; type: string }[]
  rivals: string[]
}

export interface AdminLeaguesReport {
  total: number
  system: number
  userCreated: number
  sizeBuckets: { label: string; count: number }[]
  largest: { id: string; name: string; type: string; members: number }[]
}

export interface AdminActivityReport {
  activeSessions24h: number
  signupsByDay: { day: string; count: number }[]
  predictionsByHour: { hour: string; count: number }[]
}
